import React, { useRef } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { services } from "@/data/services";
import { Eyebrow, Button, cx } from "@/components/kit";

const ease = [0.22, 1, 0.36, 1];

const SPANS = [
  "lg:col-span-7",
  "lg:col-span-5",
  "lg:col-span-5",
  "lg:col-span-7",
  "lg:col-span-4",
  "lg:col-span-4",
  "lg:col-span-4",
];
const FEATURED = new Set([0, 3]);

export const serviceObject = (slug) => `/v2/obj-${slug}.png`;

const BentoCard = ({ svc, index }) => {
  const ref = useRef(null);
  const onMove = (e) => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    el.style.setProperty("--mx", `${e.clientX - r.left}px`);
    el.style.setProperty("--my", `${e.clientY - r.top}px`);
  };
  const Icon = svc.icon;
  const featured = FEATURED.has(index);

  return (
    <motion.div
      initial={{ opacity: 0, y: 28 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.7, delay: (index % 3) * 0.06, ease }}
      className={cx("col-span-12 sm:col-span-6", SPANS[index])}
    >
      <Link
        ref={ref}
        onMouseMove={onMove}
        to={`/diensten/${svc.slug}`}
        data-testid={`service-bento-${svc.slug}`}
        className="bento-card group relative flex h-full min-h-[250px] flex-col justify-between overflow-hidden rounded-[1.75rem] border border-ivory-400 bg-white p-7 lg:p-9 shadow-card-light hover:border-electric/40 hover:shadow-card-lift hover:-translate-y-1"
      >
        {/* 3D object */}
        <div
          aria-hidden="true"
          className={cx(
            "pointer-events-none absolute transition-transform duration-500 ease-out group-hover:scale-110 group-hover:-rotate-3",
            featured
              ? "-right-8 -bottom-10 w-52 h-52 lg:w-72 lg:h-72"
              : "-right-7 -bottom-9 w-40 h-40 lg:w-52 lg:h-52"
          )}
        >
          <img
            src={serviceObject(svc.slug)}
            alt=""
            loading="lazy"
            className="w-full h-full object-contain drop-shadow-[0_24px_40px_rgba(0,56,255,0.18)]"
          />
        </div>

        <div className="relative">
          <div className="flex items-center justify-between">
            <div className="w-12 h-12 rounded-xl bg-electric/10 border border-electric/15 grid place-items-center text-electric group-hover:bg-electric group-hover:text-white transition-colors duration-300">
              <Icon className="w-5 h-5" />
            </div>
            <span className="font-mono text-[11px] tracking-[0.25em] text-coal/35">
              {String(index + 1).padStart(2, "0")}
            </span>
          </div>
          <h3
            className={cx(
              "mt-6 font-display font-bold tracking-tight text-coal",
              featured ? "text-3xl lg:text-4xl" : "text-2xl lg:text-3xl"
            )}
          >
            {svc.title}
          </h3>
          <p className={cx("mt-3 text-coal/60 leading-relaxed text-[15px]", featured ? "max-w-sm" : "max-w-[16rem]")}>
            {svc.short}
          </p>
        </div>

        <div className="relative mt-8 inline-flex items-center gap-2 text-sm font-semibold text-coal group-hover:text-electric transition-colors">
          <span className="link-draw">Bekijk dienst</span>
          <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
        </div>
      </Link>
    </motion.div>
  );
};

const ServicesSection = () => (
  <section
    id="diensten"
    data-testid="services-section"
    className="relative bg-ivory py-24 lg:py-32"
  >
    <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end mb-14 lg:mb-20">
        <div className="lg:col-span-8">
          <Eyebrow>Diensten · Zeven groeimotoren</Eyebrow>
          <h2 className="mt-6 font-display font-black text-coal tracking-tight leading-[0.95] text-4xl sm:text-5xl lg:text-6xl">
            Alles wat je nodig hebt<br className="hidden sm:block" /> om online te <span className="text-gradient-blue">winnen.</span>
          </h2>
        </div>
        <div className="lg:col-span-4 lg:text-right">
          <p className="text-coal/60 leading-relaxed mb-5 lg:ml-auto max-w-sm">
            Van strategie tot uitvoering — één partner voor je hele online groei.
          </p>
          <Button to="/diensten" variant="outline" withArrow testid="services-all-link">
            Alle diensten
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-12 gap-5 lg:gap-6">
        {services.map((svc, i) => (
          <BentoCard key={svc.slug} svc={svc} index={i} />
        ))}
      </div>
    </div>
  </section>
);

export default ServicesSection;
